import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { first, flatMap, get, indexOf, isEmpty, join, map, size } from 'lodash';
import { useSnackbar } from 'notistack';
import { default as React, ReactElement, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { runQuantification } from 'api/platform';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { getModelsTypeByModelInferences, ModelInference } from 'interfaces/calculateFeatures';
import { CellSegmentationConfig } from 'interfaces/inferenceArtifacts';
import { CellQuantificationJob, JobType } from 'interfaces/job';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';
import { getStainingMethodByStainType, StainingMethod } from 'interfaces/stainType';
import { humanize } from 'utils/helpers';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { ActionStepper } from '../ActionStepper';
import { JobWithRebuild } from '../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { buildSelectedInferenceOrchestrationsFromArtifacts } from '../PostProcessingAndCalculateFeatures/utils';
import { ExecutionRequirementsPanel } from '../requirements/ExecutionRequirementsPanel';
import { ExecutionRequirementType, useExecutionRequirements } from '../requirements/useExecutionRequirements';
import { getMapArtifactToSlides, JobNameAndDescriptionStep } from '../utils';
import { AdditionalParametersStep } from './AdditionalParametersStep';
import { SyntheticSegmentationConfigurationStep } from './SyntheticSegmentationConfigurationStep';
import { QuantificationFormValues, runQuantificationOrder, RunQuantificationStep } from './types';
import { defaultsSyntheticSegmentationForMIF, setDefaultSyntheticSegmentationValues } from './utils';

const SNACK_BAR_KEY_RUN_QUANTIFICATION = 'RUN_QUANTIFICATION';

export interface RunQuantificationProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
}

const defaultValues: QuantificationFormValues = {
  jobName: '',
  jobDescription: '',
  configParams: {
    inputArtifactUrlPatterns: [],
    quantifiedChannelNames: [],
    syntheticSegmentationConfigurationDefaultCase: null,
    cellCompartmentParams: {
      membraneOutwardsThicknessUm: 0,
      membraneInwardsThicknessUm: 0,
    },
    syntheticCytoplasmThicknessUm: null,
    syntheticNuclearRadiusUm: null,
    syntheticMembraneErosionFactor: null,
    fixedStainParams: undefined,
    manifest: [],
  },
};

const validationSchema = {
  [RunQuantificationStep.LoadParams]: yup.object({}),
  [RunQuantificationStep.NameAndDescription]: yup.object({}),
  [RunQuantificationStep.SelectInferenceModels]: yup.object().shape({
    configParams: yup.object().shape({
      inputArtifactUrlPatterns: yup
        .array()
        .of(
          yup.object().shape({
            artifactUrlPattern: yup.string().required('Artifact URL Pattern is required'),
            slideIds: yup
              .array()
              .of(yup.string().required('Slide ID is required'))
              .min(1, 'At least one Slide ID is required'),
          })
        )
        .min(1, 'At least one Artifact URL Pattern is required')
        .required('Artifact URL Pattern is required'),
    }),
  }),
  [RunQuantificationStep.AdditionalParameters]: yup.object({
    configParams: yup.object().shape({
      quantifiedChannelNames: yup
        .array()
        .of(yup.string().required('Channel name is required'))
        .min(1, 'At least one channel must be selected')
        .required('Quantified channels are required'),
    }),
  }),
  [RunQuantificationStep.SyntheticSegmentationConfiguration]: yup.object({
    configParams: yup.object().shape({
      cellCompartmentParams: yup.object().shape({
        membraneOutwardsThicknessUm: yup
          .number()
          .typeError('Please enter a valid number')
          .min(0, 'Membrane Half Thickness must be a positive number')
          .required('Membrane Outwards Thickness is required'),
        membraneInwardsThicknessUm: yup
          .number()
          .typeError('Please enter a valid number')
          .min(0, 'Membrane Half Thickness must be a positive number')
          .required('Membrane Inwards Thickness is required'),
      }),
      syntheticCytoplasmThicknessUm: yup
        .number()
        .min(0, 'Synthetic Cytoplasm Thickness must be a positive number')
        .nullable(),
      syntheticNuclearRadiusUm: yup.number().min(0, 'Synthetic Nuclear Radius must be a positive number').nullable(),
      syntheticMembraneErosionFactor: yup
        .number()
        .min(0, 'Synthetic Membrane Erosion Factor must be a positive number')
        .nullable(),
    }),
  }),
};

export const RunQuantification = (props: RunQuantificationProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const studyId = casesParams.filters?.studyId;
  const orchestrationSelectionState = useOrchestrationSelections();
  const { selectedOrchestrations, setSelectedOrchestrations } = orchestrationSelectionState;
  const [selectedJobId, setSelectedJobId] = useState<string>(undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const [pendingPreviousJobInferenceSelections, setPendingPreviousJobInferenceSelections] = useState<
    MapArtifactToSlides[] | null
  >(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const [stepWarnings, setStepWarnings] = useState<Record<number, string>>({});

  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const currentValidationSchema = validationSchema[runQuantificationOrder[activeStep]];

  const methods = useForm<QuantificationFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: defaultValues,
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = methods;
  const executionRequirements = useExecutionRequirements([
    {
      type: ExecutionRequirementType.CloudOptimizedSlides,
      label: 'Cloud-optimized slides',
      casesParams,
      slideIds: watch('configParams.manifest'),
      enabled: Boolean(studyId),
    },
  ]);

  const { data: stains, isLoading: isLoadingStains } = useQuery({
    queryKey: ['slidesStainsTypes', casesParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(casesParams, signal),
  });

  const stainingMethod = getStainingMethodByStainType(first(stains));

  useEffect(() => {
    const caseIndex =
      stainingMethod === StainingMethod.mplex
        ? defaultsSyntheticSegmentationForMIF
        : defaultsSyntheticSegmentationForMIF;
    methods.setValue('configParams.syntheticSegmentationConfigurationDefaultCase', caseIndex);

    setDefaultSyntheticSegmentationValues(caseIndex, methods);
  }, [stainingMethod]);

  const runQuantificationMutation = useMutationWithErrorSnackbar({
    mutationFn: runQuantification,
    mutationDescription: 'run cell Quantification',
    onSuccess: () => {
      enqueueSnackbar('Cell Quantification Started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_QUANTIFICATION);
    },
  });

  const checkValidationAndSetIsStepFailed = async (step: RunQuantificationStep) => {
    const stepIndex = indexOf(runQuantificationOrder, step);

    let isValid = await trigger();

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const setWarnings = (step: RunQuantificationStep) => {
    const stepIndex = indexOf(runQuantificationOrder, step);

    if (step === RunQuantificationStep.SelectInferenceModels) {
      setStepWarnings((prev) => ({
        ...prev,
        [stepIndex]:
          size(watch('configParams.manifest')) > 0 && size(watch('configParams.manifest')) < size(slides)
            ? 'Some slides don’t have cell segmentation results selected, so the job will not run on them'
            : '',
      }));
    }
  };

  const cellQuantificationArtifactsParams = {
    ...casesParams,
    configParams: CellSegmentationConfig,
  };

  const {
    data: inferenceArtifacts,
    isFetching: isInferenceArtifactsFetching,
    isError: isInferenceArtifactsError,
  } = useArtifactResultsQuery<ModelInference>({
    caseParams: casesParams,
    config: CellSegmentationConfig,
  });

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal),
    enabled: Boolean(studyId),
  });

  useEffect(() => {
    if (pendingPreviousJobInferenceSelections === null || isInferenceArtifactsFetching) {
      return;
    }

    setSelectedOrchestrations(
      buildSelectedInferenceOrchestrationsFromArtifacts(pendingPreviousJobInferenceSelections, inferenceArtifacts || [])
    );
    setPendingPreviousJobInferenceSelections(null);
  }, [
    inferenceArtifacts,
    isInferenceArtifactsFetching,
    pendingPreviousJobInferenceSelections,
    setSelectedOrchestrations,
  ]);

  useEffect(() => {
    if (pendingPreviousJobInferenceSelections !== null) {
      return;
    }

    const inferenceArtifactUrlPatternsWithSlides: MapArtifactToSlides[] = getMapArtifactToSlides(
      map(slides, 'slideId'),
      selectedOrchestrations
    );

    setValue('configParams.inputArtifactUrlPatterns', inferenceArtifactUrlPatternsWithSlides);
    setValue('configParams.manifest', flatMap(inferenceArtifactUrlPatternsWithSlides, 'slideIds'));
    trigger('configParams.inputArtifactUrlPatterns');
    setWarnings(RunQuantificationStep.SelectInferenceModels);
  }, [pendingPreviousJobInferenceSelections, selectedOrchestrations, setValue, trigger, slides]);

  const onSelectedJobParamChange = (newValue: CellQuantificationJob) => {
    if (isEmpty(newValue)) {
      setPendingPreviousJobInferenceSelections(null);
      setSelectedOrchestrations({});
      setSelectedJobId(undefined);
      reset(defaultValues);
    } else {
      setSelectedJobId(newValue.id);
      setPendingPreviousJobInferenceSelections(newValue?.params?.inputArtifactUrlPatterns || []);
      reset({
        jobName: newValue?.name,
        jobDescription: newValue?.description,
        configParams: newValue?.params,
      });
    }
  };

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(JobType.CellQuantification);

  const stepsMap = {
    [RunQuantificationStep.LoadParams]: {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.CellQuantification}
          onSelectedJob={onSelectedJobParamChange}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    [RunQuantificationStep.NameAndDescription]: {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    [RunQuantificationStep.SelectInferenceModels]: {
      label: 'Select Inference Models',
      subLabel: activeStep !== 2 && `${size(watch('configParams.manifest'))}/${size(slides)} results selected`,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {isInferenceArtifactsError && <Typography color="error">Error fetching inference artifacts</Typography>}
            {get(errors, 'configParams.inputArtifactUrlPatterns') && (
              <Typography color="error">
                {humanize(get(errors, 'configParams.inputArtifactUrlPatterns')?.message)}
              </Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={getModelsTypeByModelInferences(inferenceArtifacts)}
              inferenceModels={inferenceArtifacts}
              isLoading={isInferenceArtifactsFetching || isSlidesFetching}
              {...orchestrationSelectionState}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => {
        checkValidationAndSetIsStepFailed(RunQuantificationStep.SelectInferenceModels);
        setWarnings(RunQuantificationStep.SelectInferenceModels);
      },
    },
    [RunQuantificationStep.AdditionalParameters]: {
      label: 'Additional Parameters',
      content: (
        <AdditionalParametersStep
          casesParams={casesParams}
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(RunQuantificationStep.AdditionalParameters)
          }
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunQuantificationStep.AdditionalParameters),
    },
    [RunQuantificationStep.SyntheticSegmentationConfiguration]: {
      label: 'Synthetic Segmentation Configuration',
      content: (
        <SyntheticSegmentationConfigurationStep
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(RunQuantificationStep.SyntheticSegmentationConfiguration)
          }
        />
      ),
      onNextOrBackClick: () =>
        checkValidationAndSetIsStepFailed(RunQuantificationStep.SyntheticSegmentationConfiguration),
    },
  };

  const steps = map(runQuantificationOrder, (stepId) => stepsMap[stepId]);

  const onSubmit: SubmitHandler<any> = (data: QuantificationFormValues) => {
    runQuantificationMutation.mutate({
      ...casesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Cell Quantification to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_QUANTIFICATION,
      autoHideDuration: null,
    });
  };

  if (isLoadingStains) {
    return <CircularProgress />;
  }

  if (size(stains) !== 1) {
    return (
      <Typography>
        Please select a slide with the same stain type to run cell quantification. Currently selected stains:{' '}
        {join(
          map(stains, (stain) => {
            return stainTypeIdToDisplayName(stain);
          }),
          ', '
        )}
      </Typography>
    );
  }

  if (stainingMethod === StainingMethod.he) {
    return (
      <Typography>
        Cell quantification is not supported for HE staining. Please select a different stain type.
      </Typography>
    );
  }

  if (size(stains) === 1) {
    return (
      <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <ExecutionRequirementsPanel requirements={executionRequirements.requirements} />
          </Grid>
          <Grid item>
            <ActionStepper
              hideExecution={!executionRequirements.canExecute}
              handleSubmit={handleSubmit(onSubmit)}
              steps={steps}
              setActiveStepForValidation={setActiveStep}
              isStepFailed={isStepFailed}
              isInitializing={isCheckingPreviousJobs}
              initializingText="Checking for previous jobs…"
              validateStep={() => checkValidationAndSetIsStepFailed(runQuantificationOrder[activeStep])}
              stepWarnings={stepWarnings}
            />
          </Grid>
        </Grid>
      </JobWithRebuild>
    );
  }
};
